/**
 * CombatLogChatBridge — pumps `CombatLogService` events into the chat
 * 'combat' channel via the existing `chatHandler.combatMessage()` API.
 *
 * Glue layer between the typed combat-event stream and the chat panel.
 * No business logic — just (subscribe → filter by verbosity → format →
 * push to chat). Verbosity defaults match WoW's chat-frame combat-log:
 *
 *   'self'  (default) — events you're source or target of, plus all
 *                       kills + boss-phases + your own NPC defeats +
 *                       your own respawn. Excludes third-party hits +
 *                       third-party casts (would drown the panel).
 *
 *   'all'           — every combat event flowing through the service.
 *                     Useful for streamers + debugging. Combat tab
 *                     fills FAST under brawl.
 *
 *   'kills-only'    — only kills + npc deaths + phase changes. The
 *                     "I just want to see endings" mode.
 *
 * Singleton. Call `start()` after `CombatLogService.start()` + the room
 * is bound; call `stop()` on disconnect.
 */

import { CombatLogService } from './CombatLogService';
import { chatHandler } from '../../ui/contexts/ChatContext';
import {
  formatCombatLogEvent,
  type FormattedCombatLine,
} from '../../../shared/combat-log/CombatLogFormatter';
import type { CombatLogEvent } from '../../../shared/combat-log/CombatLogEvent';

export type CombatLogVerbosity = 'self' | 'all' | 'kills-only';

const DEFAULT_VERBOSITY: CombatLogVerbosity = 'self';

export class CombatLogChatBridge {
  private static instance: CombatLogChatBridge | null = null;

  private unsubFromService: (() => void) | null = null;
  private verbosity: CombatLogVerbosity = DEFAULT_VERBOSITY;
  private started = false;

  private constructor() { /* singleton */ }

  static getInstance(): CombatLogChatBridge {
    if (!CombatLogChatBridge.instance) {
      CombatLogChatBridge.instance = new CombatLogChatBridge();
    }
    return CombatLogChatBridge.instance;
  }

  static resetInstance(): void {
    CombatLogChatBridge.instance?.stop();
    CombatLogChatBridge.instance = null;
  }

  /** Wire the bridge. Idempotent. */
  start(): void {
    if (this.started) return;
    this.started = true;

    const svc = CombatLogService.getInstance();
    this.unsubFromService = svc.subscribe((event) => this.onCombatEvent(event));
  }

  /** Unwire the bridge. Safe to call without prior start(). */
  stop(): void {
    this.unsubFromService?.();
    this.unsubFromService = null;
    this.started = false;
  }

  setVerbosity(v: CombatLogVerbosity): void {
    this.verbosity = v;
  }

  getVerbosity(): CombatLogVerbosity {
    return this.verbosity;
  }

  // ─── Internals ────────────────────────────────────────────────

  private onCombatEvent(event: CombatLogEvent): void {
    if (!this.shouldShow(event)) return;
    const viewerSid = CombatLogService.getInstance().viewerSid();
    const formatted: FormattedCombatLine = formatCombatLogEvent(event, viewerSid);
    if (!formatted.text) return;
    chatHandler.combatMessage(formatted.text);
  }

  /**
   * Apply verbosity filter. Each verbosity level is a strict superset
   * of the previous (kills-only ⊂ self ⊂ all) — same shape as WoW's
   * chat-frame combat-log filter dropdowns.
   */
  private shouldShow(event: CombatLogEvent): boolean {
    if (this.verbosity === 'all') return true;

    if (this.verbosity === 'kills-only') {
      return event.kind === 'kill'
          || event.kind === 'npc_death'
          || event.kind === 'phase';
    }

    // 'self' — default WoW-faithful filter
    const me = CombatLogService.getInstance().viewerSid();
    switch (event.kind) {
      case 'damage':
      case 'heal':
        return !!me && (event.sourceId === me || event.targetId === me);
      case 'cast':
        return !!me && event.sourceId === me;
      case 'kill':
      case 'phase':
      case 'respawn':
        return true; // always show — sparse + significant
      case 'npc_death':
        return !!me && event.defeatedById === me;
    }
  }
}
